import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  MoreHorizontal,
  FileText,
  Loader2,
  Eye,
  Pencil,
  Trash2,
  Send,
  ArrowRightCircle,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CounterpartyBadge } from '@/components/trading/sales/counterparty-badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';

const statusConfig: Record<string, { color: string; icon: any }> = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  SENT: { color: 'bg-blue-100 text-blue-700', icon: Send },
  ACCEPTED: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  REJECTED: { color: 'bg-red-100 text-red-700', icon: XCircle },
  EXPIRED: { color: 'bg-amber-100 text-amber-700', icon: Clock },
  CONVERTED: { color: 'bg-purple-100 text-purple-700', icon: RefreshCw },
};

export function SalesQuotesPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'trading:sales-quotes:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [selectedStatus, setSelectedStatus] = useSessionState<string>(
    'trading:sales-quotes:selectedStatus',
    'all'
  );
  const [page, setPage] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<string | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.tradingSalesQuote.list.useQuery({
    search: debouncedSearch || undefined,
    status:
      selectedStatus !== 'all'
        ? (selectedStatus as 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'CONVERTED')
        : undefined,
    page,
  });

  const utils = trpc.useUtils();

  const deleteMutation = trpc.tradingSalesQuote.delete.useMutation({
    onSuccess: () => {
      utils.tradingSalesQuote.list.invalidate();
      showSuccessToast(t('common.deleted'));
      setDeleteConfirmOpen(false);
      setQuoteToDelete(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const sendMutation = trpc.tradingSalesQuote.send.useMutation({
    onSuccess: () => {
      utils.tradingSalesQuote.list.invalidate();
      showSuccessToast(t('common.updated'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const convertMutation = trpc.tradingSalesQuote.convertToOrder.useMutation({
    onSuccess: (data) => {
      utils.tradingSalesQuote.list.invalidate();
      showSuccessToast(t('common.created'));
      navigate(`/trading/sales/orders/${data.id}`);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const quotes = data?.quotes ?? [];
  const pagination = data?.pagination;

  const handleDeleteQuote = (id: string) => {
    setQuoteToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (quoteToDelete) {
      deleteMutation.mutate(quoteToDelete);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return t('sales.quotes.draft');
      case 'SENT':
        return t('sales.quotes.sent');
      case 'ACCEPTED':
        return t('sales.quotes.accepted');
      case 'REJECTED':
        return t('sales.quotes.rejected');
      case 'EXPIRED':
        return t('sales.quotes.expired');
      case 'CONVERTED':
        return t('sales.quotes.converted');
      default:
        return status;
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const statuses = ['DRAFT', 'SENT', 'ACCEPTED', 'REJECTED', 'EXPIRED', 'CONVERTED'];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('sales.quotes.title')}</h1>
          <p className="text-muted-foreground">{t('sales.quotes.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/trading/sales/quotes/new')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('sales.quotes.addQuote')}
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('sales.quotes.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('sales.quotes.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('sales.quotes.selectStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('sales.quotes.allStatuses')}</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {getStatusLabel(status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && quotes.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileText className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('sales.quotes.noQuotes')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">{t('sales.quotes.noQuotesDesc')}</p>
          <Button className="mt-4" onClick={() => navigate('/trading/sales/quotes/new')}>
            <Plus className="mr-2 h-4 w-4" />
            {t('sales.quotes.addQuote')}
          </Button>
        </div>
      )}

      {/* Quotes Table */}
      {!isLoading && !error && quotes.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('sales.quotes.quoteNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('sales.quotes.customer')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {t('sales.quotes.status')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('sales.quotes.quoteDate')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('sales.quotes.validUntil')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('sales.quotes.items')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('sales.quotes.total')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {tCommon('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {quotes.map((quote) => {
                  const config = statusConfig[quote.status] || statusConfig.DRAFT;
                  const StatusIcon = config?.icon ?? Clock;

                  return (
                    <tr key={quote.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Link
                          to={`/trading/sales/quotes/${quote.id}`}
                          className="text-primary font-mono text-sm font-medium hover:underline"
                        >
                          {quote.quoteNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {(quote as any).counterpartyName ||
                                quote.customerName ||
                                quote.contactName}
                            </span>
                            <CounterpartyBadge kind={(quote as any).counterpartyKind} />
                          </div>
                          <div className="text-muted-foreground text-xs">{quote.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {getStatusLabel(quote.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDate(quote.quoteDate)}</td>
                      <td className="px-4 py-3 text-sm">{formatDate(quote.validUntil)}</td>
                      <td className="px-4 py-3 text-right text-sm">{quote.itemCount}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        {formatCurrency(quote.total, quote.currency)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/trading/sales/quotes/${quote.id}`)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              {tCommon('view')}
                            </DropdownMenuItem>
                            {quote.status === 'DRAFT' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => navigate(`/trading/sales/quotes/${quote.id}/edit`)}
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  {tCommon('edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => sendMutation.mutate(quote.id)}>
                                  <Send className="mr-2 h-4 w-4" />
                                  {t('sales.quotes.send')}
                                </DropdownMenuItem>
                              </>
                            )}
                            {quote.status === 'ACCEPTED' && (
                              <DropdownMenuItem onClick={() => convertMutation.mutate(quote.id)}>
                                <ArrowRightCircle className="mr-2 h-4 w-4" />
                                {t('sales.quotes.convertToOrder')}
                              </DropdownMenuItem>
                            )}
                            {quote.status === 'DRAFT' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleDeleteQuote(quote.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {tCommon('delete')}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {tCommon('showing')} {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} {tCommon('of')}{' '}
                {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="border-border h-8 border px-3 text-sm disabled:opacity-50"
                >
                  {tCommon('previous')}
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="border-border h-8 border px-3 text-sm disabled:opacity-50"
                >
                  {tCommon('next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
